import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Página de Primeiro Acesso (Self-Service)
 *
 * Fluxo:
 * 1. Usuário informa email
 * 2. Sistema verifica se já possui senha (ALREADY_ACTIVE → bloqueia)
 * 3. Se não possui senha: envia código de 6 dígitos por email
 * 4. Usuário insere código e cria senha
 * 5. Sistema valida código e cria senha
 * 6. Redireciona para login
 */
export default function FirstAccess() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<"email" | "code" | "already_active">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const requestCodeMutation = trpc.auth.requestFirstAccessCode.useMutation({
    onSuccess: (data: any) => {
      // Verificar se backend retornou ALREADY_ACTIVE
      if (data.code === "ALREADY_ACTIVE") {
        setStep("already_active");
        return;
      }
      toast.success(data.message || "Código de verificação enviado para seu e-mail!");
      setStep("code");
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao enviar código");
    },
  });

  const confirmAccessMutation = trpc.auth.confirmFirstAccess.useMutation({
    onSuccess: (data) => {
      toast.success(data.message);
      setTimeout(() => {
        setLocation("/");
      }, 2000);
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao confirmar código");
    },
  });

  const handleRequestCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Por favor, informe seu email");
      return;
    }
    requestCodeMutation.mutate({ email });
  };

  const handleConfirmCode = (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || code.length !== 6) {
      toast.error("Por favor, informe o código de 6 dígitos");
      return;
    }

    if (!password || password.length < 8) {
      toast.error("A senha deve ter no mínimo 8 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    confirmAccessMutation.mutate({ email, code, password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo e Título */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime-400 text-slate-900 font-bold text-2xl mb-4">
            SI
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {step === "already_active" ? "Conta já ativada" : "Primeiro Acesso"}
          </h1>
          <p className="text-slate-400">
            {step === "email"
              ? "Informe seu email para receber o código de verificação"
              : step === "code"
              ? "Insira o código recebido e crie sua senha"
              : "Sua conta já possui senha cadastrada"}
          </p>
        </div>

        {/* Card do Formulário */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg p-8 shadow-xl">

          {/* ── Estado: ALREADY_ACTIVE ── */}
          {step === "already_active" && (
            <div className="space-y-6">
              {/* Ícone de aviso */}
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                  <svg className="w-8 h-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
              </div>

              {/* Mensagem clara */}
              <div className="text-center space-y-2">
                <p className="text-white font-medium">
                  Este usuário já possui senha cadastrada.
                </p>
                <p className="text-slate-400 text-sm">
                  Faça login normalmente ou utilize "Esqueci minha senha" para redefinir seu acesso.
                </p>
              </div>

              {/* Dois botões de ação */}
              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  onClick={() => setLocation("/")}
                >
                  Ir para Login
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-slate-600 text-slate-200 hover:bg-slate-700"
                  onClick={() => setLocation("/forgot-password")}
                >
                  Esqueci minha senha
                </Button>
              </div>

              {/* Link voltar */}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setEmail("");
                  }}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  ← Tentar outro email
                </button>
              </div>
            </div>
          )}

          {/* ── Estado 1: Solicitar Email ── */}
          {step === "email" && (
            <form onSubmit={handleRequestCode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  autoFocus
                  disabled={requestCodeMutation.isPending}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={requestCodeMutation.isPending}
              >
                {requestCodeMutation.isPending ? "Verificando..." : "Enviar Código"}
              </Button>

              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => setLocation("/")}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  ← Voltar para login
                </button>
              </div>
            </form>
          )}

          {/* ── Estado 2: Validar Código + Criar Senha ── */}
          {step === "code" && (
            <form onSubmit={handleConfirmCode} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-slate-200">
                  Código de Verificação
                </Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 text-center text-2xl tracking-widest"
                  required
                  autoFocus
                  disabled={confirmAccessMutation.isPending}
                  maxLength={6}
                />
                <p className="text-xs text-slate-400">
                  Insira o código de 6 dígitos enviado para {email}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">
                  Nova Senha
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  disabled={confirmAccessMutation.isPending}
                  minLength={8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-200">
                  Confirmar Senha
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  disabled={confirmAccessMutation.isPending}
                  minLength={8}
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-lime-400 hover:bg-lime-500 text-slate-900 font-semibold"
                disabled={confirmAccessMutation.isPending}
              >
                {confirmAccessMutation.isPending ? "Criando senha..." : "Criar Senha"}
              </Button>

              <div className="text-center pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setPassword("");
                    setConfirmPassword("");
                  }}
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                  disabled={confirmAccessMutation.isPending}
                >
                  ← Voltar
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Rodapé */}
        <div className="text-center mt-6 text-sm text-slate-500">
          Insight NR1 - Gestão de Risco Ocupacional
        </div>
      </div>
    </div>
  );
}
